// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// /fleet/demo/:scenario — the fleet overview, driven by a scripted fleet.
//
// The map's framing is a pure function of where the sites are (autoFit.ts),
// and the real fleet is a single site, so the zoom behaviour cannot be seen
// on /fleet at all. This screen renders the SAME <FleetOverview /> against
// the fixtures in fleetDemoScenarios.ts, so what is exercised here is the
// production component, not a copy of it that could drift from it.
//
// Everything scripted is fenced behind this route: the banner says so on
// every scenario, the dateline says so, and no scenario data is reachable
// from /fleet (see FleetOverview's `demo` prop).

import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { PageHead } from '../../../components/shell/ScreenHelpers';
import FleetOverview from '../FleetOverview';
import { FLEET_DEMO_SCENARIOS, findFleetDemoScenario } from './fleetDemoScenarios';
import './fleet-demo.css';

/** The scenario switcher, shown on every scenario and on the unknown-id page. */
function ScenarioLinks({ activeId }: { activeId?: string }) {
  return (
    <nav className="fleet-demo-switch" aria-label="Demo scenarios">
      {FLEET_DEMO_SCENARIOS.map(scenario => (
        <Link
          key={scenario.id}
          to={`/fleet/demo/${scenario.id}`}
          className={`fleet-demo-chip${scenario.id === activeId ? ' fleet-demo-chip--on' : ''}`}
          aria-current={scenario.id === activeId ? 'page' : undefined}
          // The chip shows just the number, so the accessible name (and the
          // tooltip) has to carry what the scenario actually is.
          aria-label={`Scenario ${scenario.id}: ${scenario.title}`}
          title={scenario.title}
        >
          {scenario.id}
        </Link>
      ))}
    </nav>
  );
}

export default function FleetOverviewDemo() {
  const { scenario: id } = useParams();
  const scenario = findFleetDemoScenario(id);

  // Neither /fleet/demo nor an unknown id picks a scenario for you: silently
  // showing a fleet the URL never asked for is exactly how someone ends up
  // reporting the wrong scenario's framing as a bug — and on the bare route,
  // how a scripted fleet gets mistaken for the live one.
  if (!scenario) {
    return (
      <>
        <PageHead
          title="Fleet overview"
          dateline={id ? 'Demo · unknown scenario' : 'Demo · scenario index'}
        />
        <div className="fleet-demo-banner fleet-demo-banner--unknown">
          <div className="fleet-demo-banner-title">
            {id ? <>There is no demo scenario &ldquo;{id}&rdquo;.</> : <>Map framing scenarios</>}
          </div>
          <div className="fleet-demo-banner-blurb">
            {id ? 'Pick one of the' : 'Each one frames the map differently. Pick one of the'}{' '}
            {FLEET_DEMO_SCENARIOS.length} below.
          </div>
          <ul className="fleet-demo-list">
            {FLEET_DEMO_SCENARIOS.map(s => (
              <li key={s.id}>
                <Link to={`/fleet/demo/${s.id}`}>
                  {s.id} — {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="fleet-demo-banner" data-testid="fleet-demo-banner">
        <div className="fleet-demo-banner-head">
          <span className="fleet-demo-tag">Demo</span>
          <span className="fleet-demo-banner-title">{scenario.title}</span>
          <ScenarioLinks activeId={scenario.id} />
        </div>
        <p className="fleet-demo-banner-blurb">{scenario.blurb}</p>
        {/* Said plainly, on every scenario: none of these sites or counts are
            real. Coordinates are real city centres so the markers land where
            a reader expects. */}
        <p className="fleet-demo-banner-note">
          Scripted fleet for testing the map&rsquo;s framing. Every scenario is a subset of the same
          five sites; the sites, their alarm counts and their postures are invented, and only the
          coordinates are real. Nothing here is fetched, and drilling into a site is disabled.
        </p>
      </div>
      <FleetOverview demo={scenario} />
    </>
  );
}
